import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Check, Info, Clock, ShieldCheck, Sparkles, Download, Sliders, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";

export const ServiceInclusionsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"standard" | "deep" | "moveInOut">("standard");
  const [showOffer, setShowOffer] = useState(false);
  const [toastMessage, setToastMessage] = useState<{name: string, service: string, time: string} | null>(null);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      setShowOffer(true);
    };
    
    window.addEventListener("open-service-inclusions", handleOpen);
    return () => window.removeEventListener("open-service-inclusions", handleOpen);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    
    // Simulate live bookings
    const bookings = [
      { name: "Sarah M.", service: "Deep Clean", time: "2 min ago" },
      { name: "Michael T.", service: "Standard Clean", time: "5 min ago" },
      { name: "Jessica R.", service: "Deep Clean", time: "Just now" },
      { name: "David L.", service: "Move In/Out", time: "12 min ago" },
    ];
    
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      setToastMessage(bookings[currentIndex]);
      currentIndex = (currentIndex + 1) % bookings.length;
      
      setTimeout(() => {
        setToastMessage(null);
      }, 4000);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  const checklists = {
    standard: [
      { room: "Kitchen", items: ["Wipe counters & cabinets", "Clean sink & faucet", "Wipe exterior appliances", "Clean microwave inside/out", "Sweep & mop floors", "Empty trash"] },
      { room: "Bathrooms", items: ["Clean & disinfect toilets", "Scrub showers & tubs", "Clean sinks & counters", "Clean mirrors", "Sweep & mop floors", "Empty trash"] },
      { room: "Living Areas", items: ["Dust all accessible surfaces", "Wipe tables & chairs", "Vacuum carpets & rugs", "Sweep & mop hard floors", "Tidy up general areas"] },
      { room: "Bedrooms", items: ["Dust surfaces & nightstands", "Make beds (if linens left out)", "Vacuum floors", "Empty trash", "Wipe mirrors"] },
    ],
    deep: [
      { room: "Everything in Standard, PLUS:", items: ["Wet wipe baseboards", "Clean interior windows & sills", "Dust ceiling fans & vents", "Wipe door frames & handles", "Extra attention to heavy buildup"] },
    ],
    moveInOut: [
      { room: "Everything in Deep Clean, PLUS:", items: ["Clean inside all cabinets", "Clean inside all drawers", "Clean inside refrigerator", "Clean inside oven", "Ensure home is move-in ready"] },
    ]
  };

  const totalTasks = activeTab === "standard" ? 22 : activeTab === "deep" ? 27 : 32;
  const maxTasks = 32;
  const progressValue = (totalTasks / maxTasks) * 100;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Uplevel Cleaning Checklist',
          text: `Check out what's included in the ${activeTab} cleaning plan!`,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent id="printable-checklist" className="max-w-4xl p-0 overflow-hidden bg-slate-50 dark:bg-background rounded-3xl border-border">
        
        {/* Header Section */}
        <div className="bg-primary p-6 text-primary-foreground relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-cover bg-center mix-blend-overlay" style={{ backgroundImage: "url('https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=800&q=80')" }}></div>
          <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-transparent"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <DialogTitle className="text-2xl sm:text-3xl font-extrabold mb-1">Interactive Cleaning Checklist</DialogTitle>
              <p className="text-primary-foreground/80 font-medium">See exactly what's included in each package.</p>
            </div>
            
            {/* Toggle */}
            <div className="bg-background/20 p-1 rounded-xl flex items-center shrink-0 w-fit backdrop-blur-sm border border-white/10 overflow-x-auto max-w-full hide-scrollbar print:hidden">
              <button 
                onClick={() => setActiveTab("standard")}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 whitespace-nowrap",
                  activeTab === "standard" ? "bg-white text-primary shadow-md scale-105" : "text-white hover:bg-white/10"
                )}
              >
                Standard
              </button>
              <button 
                onClick={() => setActiveTab("deep")}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap",
                  activeTab === "deep" ? "bg-white text-primary shadow-md scale-105" : "text-white hover:bg-white/10"
                )}
              >
                Deep Clean <Sparkles className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={() => setActiveTab("moveInOut")}
                className={cn(
                  "px-3 py-2 rounded-lg text-sm font-bold transition-all duration-300 whitespace-nowrap",
                  activeTab === "moveInOut" ? "bg-white text-primary shadow-md scale-105" : "text-white hover:bg-white/10"
                )}
              >
                Move In/Out
              </button>
            </div>
          </div>
        </div>

        {/* Time Limited Offer Banner */}
        {showOffer && (
          <div className="bg-accent text-accent-foreground px-4 py-2.5 text-sm font-bold flex items-center justify-center gap-2 animate-in slide-in-from-top-2 print:hidden">
            <Clock className="w-4 h-4 animate-pulse" />
            <span>Limited Time: Get 10% OFF your first clean with code <span className="bg-white/20 px-1.5 py-0.5 rounded">UPLEVEL10</span></span>
          </div>
        )}

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          
          <div className="mb-6 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-1">
                {activeTab === "standard" ? "Best for routine maintenance" : activeTab === "deep" ? "Highly recommended for first-time customers" : "Perfect for empty homes"}
              </h4>
              <p className="text-sm text-blue-800/80 dark:text-blue-300/80">
                {activeTab === "standard" 
                  ? "Standard cleaning is designed to keep your home fresh and tidy on a regular basis. It assumes the home has been professionally deep cleaned recently."
                  : activeTab === "deep" ? "Deep cleaning tackles build-up, baseboards, and areas that regular cleaning misses. It creates a perfect baseline for future standard cleanings." : "Comprehensive cleaning for empty homes, ensuring you get your deposit back or move into a spotless new space."}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between text-sm font-bold mb-2">
              <span className="text-muted-foreground">Comprehensive Checklist</span>
              <span className="text-primary">{totalTasks} Tasks Included</span>
            </div>
            <Progress value={progressValue} className="h-2" />
          </div>

          {/* Desktop Grid View */}
          <div className="hidden md:grid sm:grid-cols-2 gap-6">
            {checklists.standard.map((category, idx) => (
              <div key={idx} className="bg-white dark:bg-card border-2 border-muted rounded-2xl p-5 shadow-sm">
                <h3 className="font-bold text-lg mb-4 text-primary border-b pb-2">
                  {category.room}
                </h3>
                <ul className="space-y-3">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm md:text-base">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            
            {activeTab !== "standard" && checklists[activeTab === "deep" ? "deep" : "moveInOut"].map((category, idx) => (
               <div key={`extra-${idx}`} className="bg-primary/5 border-2 border-primary rounded-2xl p-5 shadow-sm sm:col-span-2">
               <h3 className="font-bold text-lg mb-4 text-primary border-b border-primary/20 pb-2 flex items-center gap-2">
                 <Sparkles className="w-5 h-5" /> {category.room}
               </h3>
               <ul className="grid sm:grid-cols-2 gap-3">
                 {category.items.map((item, i) => (
                   <li key={i} className="flex items-start gap-3 text-sm md:text-base font-medium">
                     <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                     <span>{item}</span>
                   </li>
                 ))}
               </ul>
             </div>
            ))}
          </div>

          {/* Mobile Accordion View */}
          <div className="md:hidden mt-4">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {checklists.standard.map((category, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="bg-white dark:bg-card border-2 border-muted rounded-2xl px-5 shadow-sm">
                  <AccordionTrigger className="font-bold text-lg text-primary hover:no-underline py-4">
                    {category.room}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-3 pb-4">
                      {category.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm font-medium">
                          <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
              
              {activeTab !== "standard" && checklists[activeTab === "deep" ? "deep" : "moveInOut"].map((category, idx) => (
                <AccordionItem key={`extra-${idx}`} value={`extra-${idx}`} className="bg-primary/5 border-2 border-primary rounded-2xl px-5 shadow-sm">
                  <AccordionTrigger className="font-bold text-lg text-primary hover:no-underline py-4 flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" /> {category.room}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-3 pb-4">
                      {category.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm font-medium">
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-muted/50 p-4 rounded-2xl border border-border print:hidden">
            <div className="flex items-center gap-3 text-sm font-medium text-muted-foreground">
              <ShieldCheck className="w-5 h-5 text-green-500 shrink-0" />
              <span>Backed by our 200% Satisfaction Guarantee</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl bg-white dark:bg-card" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-xl bg-white dark:bg-card" onClick={() => window.print()}>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button asChild size="lg" className="w-full sm:w-auto rounded-xl shadow-lg btn-pulse">
                <a href={`/book?view=custom-plan&service=${activeTab}`}>
                  <Sliders className="w-4 h-4 mr-2" />
                  Customize Plan
                </a>
              </Button>
            </div>
          </div>
        </div>

        {/* Live Notification Toast */}
        <div className={cn(
          "absolute bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-border p-3 flex items-center gap-3 transition-all duration-500 transform z-50",
          toastMessage ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0 pointer-events-none"
        )}>
          <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center shrink-0">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">
              {toastMessage?.name} booked a <span className="text-primary">{toastMessage?.service}</span>
            </p>
            <p className="text-xs text-muted-foreground font-medium">{toastMessage?.time}</p>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};
